import { themeColors } from "./core";
import { IMenuItem } from "./Interfaces/options";
import toast from "react-hot-toast";
import { useState, useEffect, useCallback } from "react";
import { useLocale } from "./i18n/localeContext";
import { getCommonMessagePage } from "./Api/content";

// Helper function to create skeleton or content
const createSkeletonOrContent = (isLoading: boolean, content: string, fallback?: string) => {
  if (isLoading && !content) return "";
  if (!content && fallback) return fallback;
  return content || fallback || "";
};

export const getStudentMenuItems = (navbarPage: any, isLoadingContent = false): IMenuItem[] => [
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.home_stu, "Inicio"), link: "/", icon: "" },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.subject_stu, "Materias"), link: "/subjects-list", icon: "" },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.enrolled_stu, "Matrícula"),
    link: "/subjects-registration/initial",
    icon: "",
    exactLink: "subjects-registration",
  },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.box_stu, "Caja"), link: "/cart/general", icon: "", exactLink: "cart" },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.calendar_stu, "Calendario"), link: "/calendar", icon: "" },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.applications, "Solicitudes"), link: "/requests", icon: "" },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.reports_stu, "Reportes"), link: "/reports", icon: "" },
];

export const getAdminMenuItems = (navbarPage: any, isLoadingContent = false): IMenuItem[] => [
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.home_admin, "Inicio"),
    link: "/",
    icon: "",
    roles: [
      "cajero",
      "administrador(a) del sistema",
      "perfil financiero",
      "vicerrectoria",
      "matricula administrativa",
    ],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.home_admin, "Inicio"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.sponsors_admin, "Patrocinios"),
    link: "/admin/sponsorships",
    icon: "",
    roles: ["perfil financiero"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.sponsors_admin, "Patrocinios"),
  },
  // {
  //   name: "Transferencias",
  //   link: "/admin/reviews",
  //   icon: "",
  //   roles: ["perfil financiero"],
  //   tooltip: "Transferencias",
  // },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.import_admin, "Importación"),
    link: "/admin/data-imports",
    icon: "",
    roles: ["vicerrectoria"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.import_admin, "Importaciones de datos"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.box_admin, "Caja"),
    link: "/admin/charges",
    icon: "",
    roles: ["cajero"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.box_admin, "Caja administrativa"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.loginas_admin, "Ingresar como"),
    link: "admin/user-impersonation",
    icon: "",
    roles: ["administrador(a) del sistema"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.loginas_admin, "Ingresar como usuario estudiante"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.loginas_stud, "Ingreso prof."),
    link: "admin/teacher-impersonation",
    icon: "",
    roles: ["director de escuela"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.loginas_stud, "Ingresar como usuario docente"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.prenrolled_admin, "Pre-matrícula"),
    link: "admin/user-selection",
    icon: "",
    roles: ["matricula administrativa"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.prenrolled_admin, "Pre-matrícula"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.withdraw_adm, "Retiro admin"),
    link: "/admin/subjects-withdraw",
    icon: "",
    roles: ["retiro administrativo"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.withdraw_adm, "Retiro administrativo"),
  },
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.setting_admin, "Configuración"),
    link: "/admin/settings",
    icon: "",
    roles: ["administrador(a) del sistema"],
    tooltip: createSkeletonOrContent(isLoadingContent, navbarPage?.setting_admin, "Configuraciones"),
  },
];
export const getTeacherMenuItems = (navbarPage: any, isLoadingContent = false): IMenuItem[] => [
  {
    name: createSkeletonOrContent(isLoadingContent, navbarPage?.home_profesor, "Inicio"),
    link: "/",
    icon: "",
  },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.couses_profesor, "Cursos"), link: "teacher/courses", icon: "" },
  { name: createSkeletonOrContent(isLoadingContent, navbarPage?.applications, "Solicitudes"), link: "/requests", icon: "" },
];

const breakpoints = {
  xs: "320px",
  sm: "480px",
  md: "600px",
  lg: "801px",
  xl: "1025px",
  "2xl": "1281px",
};

export const devices = {
  xs: `(max-width: ${breakpoints.sm})`,
  sm: `(min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.xl})`,
  md: `(max-width: ${breakpoints.lg})`,
  lg: `(max-width: ${breakpoints.xl})`,
  xl: `(min-width: ${breakpoints.xl})`,
  "2xl": `(min-width: ${breakpoints["2xl"]})`,
};

export const horas = [
  {
    twentyFour: "08:00",
    twelve: "08:00 AM",
  },
  {
    twentyFour: "09:00",
    twelve: "09:00 AM",
  },
  {
    twentyFour: "10:00",
    twelve: "10:00 AM",
  },
  {
    twentyFour: "11:00",
    twelve: "11:00 AM",
  },
  {
    twentyFour: "12:00",
    twelve: "12:00 PM",
  },
  {
    twentyFour: "13:00",
    twelve: "01:00 PM",
  },
  {
    twentyFour: "14:00",
    twelve: "02:00 PM",
  },
  {
    twentyFour: "15:00",
    twelve: "03:00 PM",
  },
  {
    twentyFour: "16:00",
    twelve: "04:00 PM",
  },
  {
    twentyFour: "17:00",
    twelve: "05:00 PM",
  },
  {
    twentyFour: "18:00",
    twelve: "06:00 PM",
  },
  {
    twentyFour: "19:00",
    twelve: "07:00 PM",
  },
  {
    twentyFour: "20:00",
    twelve: "08:00 PM",
  },
  {
    twentyFour: "21:00",
    twelve: "09:00 PM",
  },
  {
    twentyFour: "22:00",
    twelve: "10:00 PM",
  },
];

export const weekDays = [
  { diaId: 7, acr: "Do", name: "Domingo" },
  { diaId: 1, acr: "Lun", name: "Lunes" },
  { diaId: 2, acr: "Mar", name: "Martes" },
  { diaId: 3, acr: "Mie", name: "Miercoles" }, //falta acento en miercoles en backend
  { diaId: 4, acr: "Jue", name: "Jueves" },
  { diaId: 5, acr: "Vie", name: "Viernes" },
  { diaId: 6, acr: "Sa", name: "Sábado" },
];

export const toastSuccessStyle = {
  style: {
    padding: "16px",
    color: `${themeColors.primary500}`,
    fontSize: 15,
    fontWeight: 700,
  },
  iconTheme: {
    primary: `${themeColors.primary500}`,
    secondary: "#FFF",
  },
};

export const toastErrorStyle = {
  style: {
    padding: "16px",
    color: "#e74c3c",
    width: "500px",
    fontSize: 15,
    fontWeight: 700,
  },
  iconTheme: {
    primary: "#e74c3c",
    secondary: "#FFF",
  },
};

// Fallback messages constants
export const SUCCESS_REGISTER_MESSAGE = "Registro almacenado exitósamente";
export const SUCCESS_UPDATE_MESSAGE = "Datos actualizados correctamente.";
export const SUCCESS_TRANSLATIONS_SAVED = "Traducciones guardadas exitósamente";
export const SUCCESS_SETTINGS_UPDATED = "Configuraciones actualizadas exitósamente";
export const SUCCESS_SELECTION_UPDATED = "Proceso de selección actualizado correctamente";
export const SUCCESS_OPERATION_COMPLETED = "¡Operación realizada correctamente!";
export const SUCCESS_PAYMENT_COMPLETED = "Pago realizado correctamente";
export const SUCCESS_TRANSFER_ENDORSEMENT_SAVED = "Aval de transferencia almacenado correctamente.";
export const SUCCESS_FILE_UPLOADED = "¡Archivo subido exitosamente!";
export const SUCCESS_SUBJECT_WITHDRAWN = "¡Asignatura retirada correctamente!";
export const SUCCESS_ENROLLMENT_CANCELLED = "¡Matrícula anulada correctamente!";
export const SUCCESS_SECTION_CHANGED = "¡Sección cambiada correctamente!";
export const SUCCESS_CHANGES_COMPLETED = "Cambios realizados correctamente";
export const SUCCESS_STATE_UPDATED = "Estado actualizado correctamente.";
export const SUCCESS_POINTS_UPDATED = "Puntos posibles actualizados exitósamente";
export const SUCCESS_ACTIVITY_REMOVED = "Actividad removida exitósamente";
export const SUCCESS_REQUEST_CREATED = "¡Petición creada correctamente!";
export const SUCCESS_REQUEST_DELETED = "¡Solicitud eliminada correctamente!";
export const SUCCESS_SERVICE_UPDATED = "Servicio actualizado correctamente";
export const SUCCESS_DATA_IMPORTED = "Datos importados correctamente";
export const SUCCESS_SPONSORSHIP_UPDATED = "Patrocinio actualizado correctamente";
export const SUCCESS_SPONSORSHIPS_REMOVED = "Patrocinios removidos correctamente";
export const SUCCESS_SPONSORSHIPS_UPLOADED = "Patrocinios subidos correctamente";
export const SUCCESS_GRADES_SAVED = "Calificaciones almacenadas correctamente";
export const SUCCESS_GRADES_UPDATED = "Calificaciones actualizadas correctamente";
export const SUCCESS_ATTENDANCE_UPDATED = "Asistencia actualizada correctamente";
export const SUCCESS_TRANSFER_STATE_CHANGED = "Estado de transferencia cambiado";
export const SUCCESS_SUBJECTS_WEIGHT_UPDATED = "Los pesos de las asignaturas se actualizaron correctamente";

export const ERROR_SAVING_DATA = "Ha ocurrido un error almacenando los datos.";
export const ERROR_UPDATING_BANK_STATE = "Ha ocurrido un error actualizando el estado del banco.";
export const ERROR_UPDATING_USER_STATE = "Ha ocurrido un error actualizando el estado del usuario.";
export const ERROR_LOGIN = "Ha ocurrido un error iniciando sesión, Intente de nuevo.";
export const ERROR_GENERIC = "Ha ocurrido un error. Intente de nuevo";
export const ERROR_SELECT_TEMPLATE = "Selecciona una plantilla en la lista";
export const ERROR_FIELD_NOT_FOUND = "No se pudo encontrar el campo seleccionado";
export const ERROR_UNAUTHORIZED = "Usuario no autorizado.";
export const ERROR_FIELDS_NEED_REVIEW = "Algunos campos necesitan ser revisados.";
export const ERROR_SAVE_TEMPLATE_FIRST = "Debe guardar la plantilla primero antes de traducirla";
export const ERROR_STUDENT_ID_REQUIRED = "El ID del estudiante es necesario.";
export const ERROR_TEACHER_EMAIL_REQUIRED = "El correo del docente es necesario.";
export const ERROR_SELECT_WITHDRAWAL_REASON = "Debe seleccionar un motivo de retiro";
export const ERROR_SELECT_PERIOD = "Debe seleccionar un Periodo";
export const ERROR_SAVING_TRANSFER_ENDORSEMENT = "Ha habido un error guardando el aval de transferencia. Intente otra vez.";
export const ERROR_FILE_UPLOAD_FAILED = "¡La carga del archivo ha fallado!";

export const showSuccessRegisterToast = () => {
  return toast.success(SUCCESS_REGISTER_MESSAGE, toastSuccessStyle);
};

export const useSuccessRegisterToast = () => {
  const { locale } = useLocale();
  const [commonMessage, setCommonMessage] = useState<any>(null);

  useEffect(() => {
    getCommonMessagePage(locale).then((res) => {
      const data = res?.data?.attributes || res?.data || res;
      setCommonMessage(data);
    }).catch(() => {
      setCommonMessage(null);
    });
  }, [locale]);

  return useCallback(() => {
    const message = commonMessage?.save_success_message || SUCCESS_REGISTER_MESSAGE;
    return toast.success(message, toastSuccessStyle);
  }, [commonMessage]);
};

// Hook genérico para crear hooks de toast con mensajes traducidos
const createToastHook = (
  apiKey: string,
  fallbackMessage: string,
  toastType: "success" | "error"
) => {
  return () => {
    const { locale } = useLocale();
    const [commonMessage, setCommonMessage] = useState<any>(null);

    useEffect(() => {
      getCommonMessagePage(locale).then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        setCommonMessage(data);
      }).catch(() => {
        setCommonMessage(null);
      });
    }, [locale]);

    return useCallback(() => {
      const message = commonMessage?.[apiKey] || fallbackMessage;
      if (toastType === "success") {
        return toast.success(message, toastSuccessStyle);
      } else {
        return toast.error(message, toastErrorStyle);
      }
    }, [commonMessage]);
  };
};

// Success toast hooks
export const useSuccessUpdateToast = createToastHook(
  "success_update_message",
  SUCCESS_UPDATE_MESSAGE,
  "success"
);

export const useSuccessTranslationsSavedToast = createToastHook(
  "success_translations_saved",
  SUCCESS_TRANSLATIONS_SAVED,
  "success"
);

export const useSuccessSettingsUpdatedToast = createToastHook(
  "success_settings_updated",
  SUCCESS_SETTINGS_UPDATED,
  "success"
);

export const useSuccessSelectionUpdatedToast = createToastHook(
  "success_selection_updated",
  SUCCESS_SELECTION_UPDATED,
  "success"
);

export const useSuccessOperationCompletedToast = createToastHook(
  "success_operation_completed",
  SUCCESS_OPERATION_COMPLETED,
  "success"
);

export const useSuccessPaymentCompletedToast = createToastHook(
  "success_payment_completed",
  SUCCESS_PAYMENT_COMPLETED,
  "success"
);

export const useSuccessTransferEndorsementSavedToast = createToastHook(
  "success_transfer_endorsement_saved",
  SUCCESS_TRANSFER_ENDORSEMENT_SAVED,
  "success"
);

export const useSuccessFileUploadedToast = createToastHook(
  "success_file_uploaded",
  SUCCESS_FILE_UPLOADED,
  "success"
);

export const useSuccessSubjectWithdrawnToast = createToastHook(
  "success_subject_withdrawn",
  SUCCESS_SUBJECT_WITHDRAWN,
  "success"
);

export const useSuccessEnrollmentCancelledToast = createToastHook(
  "success_enrollment_cancelled",
  SUCCESS_ENROLLMENT_CANCELLED,
  "success"
);

export const useSuccessSectionChangedToast = createToastHook(
  "success_section_changed",
  SUCCESS_SECTION_CHANGED,
  "success"
);

export const useSuccessChangesCompletedToast = createToastHook(
  "success_changes_completed",
  SUCCESS_CHANGES_COMPLETED,
  "success"
);

export const useSuccessStateUpdatedToast = createToastHook(
  "success_state_updated",
  SUCCESS_STATE_UPDATED,
  "success"
);

export const useSuccessPointsUpdatedToast = createToastHook(
  "success_points_updated",
  SUCCESS_POINTS_UPDATED,
  "success"
);

export const useSuccessActivityRemovedToast = createToastHook(
  "success_activity_removed",
  SUCCESS_ACTIVITY_REMOVED,
  "success"
);

export const useSuccessRequestCreatedToast = createToastHook(
  "success_request_created",
  SUCCESS_REQUEST_CREATED,
  "success"
);

export const useSuccessRequestDeletedToast = createToastHook(
  "success_request_deleted",
  SUCCESS_REQUEST_DELETED,
  "success"
);

export const useSuccessServiceUpdatedToast = createToastHook(
  "success_service_updated",
  SUCCESS_SERVICE_UPDATED,
  "success"
);

export const useSuccessDataImportedToast = createToastHook(
  "success_data_imported",
  SUCCESS_DATA_IMPORTED,
  "success"
);

export const useSuccessSponsorshipUpdatedToast = createToastHook(
  "success_sponsorship_updated",
  SUCCESS_SPONSORSHIP_UPDATED,
  "success"
);

export const useSuccessSponsorshipsRemovedToast = createToastHook(
  "success_sponsorships_removed",
  SUCCESS_SPONSORSHIPS_REMOVED,
  "success"
);

export const useSuccessSponsorshipsUploadedToast = createToastHook(
  "success_sponsorships_uploaded",
  SUCCESS_SPONSORSHIPS_UPLOADED,
  "success"
);

export const useSuccessGradesSavedToast = createToastHook(
  "success_grades_saved",
  SUCCESS_GRADES_SAVED,
  "success"
);

export const useSuccessGradesUpdatedToast = createToastHook(
  "success_grades_updated",
  SUCCESS_GRADES_UPDATED,
  "success"
);

export const useSuccessAttendanceUpdatedToast = createToastHook(
  "success_attendance_updated",
  SUCCESS_ATTENDANCE_UPDATED,
  "success"
);

export const useSuccessTransferStateChangedToast = createToastHook(
  "success_transfer_state_changed",
  SUCCESS_TRANSFER_STATE_CHANGED,
  "success"
);

export const useSuccessSubjectsWeightUpdatedToast = createToastHook(
  "success_subjects_weight_updated",
  SUCCESS_SUBJECTS_WEIGHT_UPDATED,
  "success"
);

// Hook para mensaje de archivo subido con nombre dinámico
export const useSuccessFileUploadedWithNameToast = () => {
  const { locale } = useLocale();
  const [commonMessage, setCommonMessage] = useState<any>(null);

  useEffect(() => {
    getCommonMessagePage(locale).then((res) => {
      const data = res?.data?.attributes || res?.data || res;
      setCommonMessage(data);
    }).catch(() => {
      setCommonMessage(null);
    });
  }, [locale]);

  return useCallback((fileName?: string) => {
    const baseMessage = commonMessage?.success_file_uploaded || SUCCESS_FILE_UPLOADED;
    const message = fileName ? `¡${fileName} subido exitosamente!` : baseMessage;
    return toast.success(message, toastSuccessStyle);
  }, [commonMessage]);
};

// Hook para mensaje de error de carga de archivo con nombre dinámico
export const useErrorFileUploadFailedWithNameToast = () => {
  const { locale } = useLocale();
  const [commonMessage, setCommonMessage] = useState<any>(null);

  useEffect(() => {
    getCommonMessagePage(locale).then((res) => {
      const data = res?.data?.attributes || res?.data || res;
      setCommonMessage(data);
    }).catch(() => {
      setCommonMessage(null);
    });
  }, [locale]);

  return useCallback((fileName?: string) => {
    const baseMessage = commonMessage?.error_file_upload_failed || ERROR_FILE_UPLOAD_FAILED;
    const message = fileName ? `¡La carga del archivo ${fileName} ha fallado!` : baseMessage;
    return toast.error(message, toastErrorStyle);
  }, [commonMessage]);
};

// Error toast hooks
export const useErrorSavingDataToast = createToastHook(
  "error_saving_data",
  ERROR_SAVING_DATA,
  "error"
);

export const useErrorUpdatingBankStateToast = createToastHook(
  "error_updating_bank_state",
  ERROR_UPDATING_BANK_STATE,
  "error"
);

export const useErrorUpdatingUserStateToast = createToastHook(
  "error_updating_user_state",
  ERROR_UPDATING_USER_STATE,
  "error"
);

export const useErrorLoginToast = createToastHook(
  "error_login",
  ERROR_LOGIN,
  "error"
);

export const useErrorGenericToast = createToastHook(
  "error_generic",
  ERROR_GENERIC,
  "error"
);

export const useErrorSelectTemplateToast = createToastHook(
  "error_select_template",
  ERROR_SELECT_TEMPLATE,
  "error"
);

export const useErrorFieldNotFoundToast = createToastHook(
  "error_field_not_found",
  ERROR_FIELD_NOT_FOUND,
  "error"
);

export const useErrorUnauthorizedToast = createToastHook(
  "error_unauthorized",
  ERROR_UNAUTHORIZED,
  "error"
);

export const useErrorFieldsNeedReviewToast = createToastHook(
  "error_fields_need_review",
  ERROR_FIELDS_NEED_REVIEW,
  "error"
);

export const useErrorSaveTemplateFirstToast = createToastHook(
  "error_save_template_first",
  ERROR_SAVE_TEMPLATE_FIRST,
  "error"
);

export const useErrorStudentIdRequiredToast = createToastHook(
  "error_student_id_required",
  ERROR_STUDENT_ID_REQUIRED,
  "error"
);

export const useErrorTeacherEmailRequiredToast = createToastHook(
  "error_teacher_email_required",
  ERROR_TEACHER_EMAIL_REQUIRED,
  "error"
);

export const useErrorSelectWithdrawalReasonToast = createToastHook(
  "error_select_withdrawal_reason",
  ERROR_SELECT_WITHDRAWAL_REASON,
  "error"
);

export const useErrorSelectPeriodToast = createToastHook(
  "error_select_period",
  ERROR_SELECT_PERIOD,
  "error"
);

export const useErrorSavingTransferEndorsementToast = createToastHook(
  "error_saving_transfer_endorsement",
  ERROR_SAVING_TRANSFER_ENDORSEMENT,
  "error"
);

export const useErrorFileUploadFailedToast = createToastHook(
  "error_file_upload_failed",
  ERROR_FILE_UPLOAD_FAILED,
  "error"
);

export const toastWarningStyle = {
  style: {
    padding: "16px",
    color: "#e0ae00",
    fontSize: 17,
    fontWeight: 700,
  },
  iconTheme: {
    primary: "#e0ae00",
    secondary: "#FFF",
  },
};

export const NUMBER = "Numero";
export const SHORT_TEXT = "Texto Corto";
export const LONG_TEXT = "Texto Largo";
export const BOOLEAN = "Booleano";
export const EMAIL = "Email";
export const OPTIONS = "Opciones";
export const MULTI_OPTIONS = "MultiOpciones";
export const EDITOR = "Editor";
export const CODE_EDITOR = "CodeEditor";
export const FECHA = "Fecha";
export const RADIO = "Radio";
export const CLAVE = "Clave";

export const COLORS = {
  calendar: {
    "1": {
      background: "#ac725e",
      foreground: "#1d1d1d",
    },
    "2": {
      background: "#d06b64",
      foreground: "#1d1d1d",
    },
    "3": {
      background: "#f83a22",
      foreground: "#1d1d1d",
    },
    "4": {
      background: "#fa573c",
      foreground: "#1d1d1d",
    },
    "5": {
      background: "#ff7537",
      foreground: "#1d1d1d",
    },
    "6": {
      background: "#ffad46",
      foreground: "#1d1d1d",
    },
    "7": {
      background: "#42d692",
      foreground: "#1d1d1d",
    },
    "8": {
      background: "#16a765",
      foreground: "#1d1d1d",
    },
    "9": {
      background: "#7bd148",
      foreground: "#1d1d1d",
    },
    "10": {
      background: "#b3dc6c",
      foreground: "#1d1d1d",
    },
    "11": {
      background: "#fbe983",
      foreground: "#1d1d1d",
    },
    "12": {
      background: "#fad165",
      foreground: "#1d1d1d",
    },
    "13": {
      background: "#92e1c0",
      foreground: "#1d1d1d",
    },
    "14": {
      background: "#9fe1e7",
      foreground: "#1d1d1d",
    },
    "15": {
      background: "#9fc6e7",
      foreground: "#1d1d1d",
    },
    "16": {
      background: "#4986e7",
      foreground: "#1d1d1d",
    },
    "17": {
      background: "#9a9cff",
      foreground: "#1d1d1d",
    },
    "18": {
      background: "#b99aff",
      foreground: "#1d1d1d",
    },
    "19": {
      background: "#c2c2c2",
      foreground: "#1d1d1d",
    },
    "20": {
      background: "#cabdbf",
      foreground: "#1d1d1d",
    },
    "21": {
      background: "#cca6ac",
      foreground: "#1d1d1d",
    },
    "22": {
      background: "#f691b2",
      foreground: "#1d1d1d",
    },
    "23": {
      background: "#cd74e6",
      foreground: "#1d1d1d",
    },
    "24": {
      background: "#a47ae2",
      foreground: "#1d1d1d",
    },
  },
  event: {
    "1": {
      background: "#a4bdfc",
      foreground: "#1d1d1d",
    },
    "2": {
      background: "#7ae7bf",
      foreground: "#1d1d1d",
    },
    "3": {
      background: "#dbadff",
      foreground: "#1d1d1d",
    },
    "4": {
      background: "#ff887c",
      foreground: "#1d1d1d",
    },
    "5": {
      background: "#fbd75b",
      foreground: "#1d1d1d",
    },
    "6": {
      background: "#ffb878",
      foreground: "#1d1d1d",
    },
    "7": {
      background: "#46d6db",
      foreground: "#1d1d1d",
    },
    "8": {
      background: "#e1e1e1",
      foreground: "#1d1d1d",
    },
    "9": {
      background: "#5484ed",
      foreground: "#1d1d1d",
    },
    "10": {
      background: "#51b749",
      foreground: "#1d1d1d",
    },
    "11": {
      background: "#dc2127",
      foreground: "#1d1d1d",
    },
  },
};

export const fallbackImage =
  "data:image/png;base64,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";